import clsx from 'clsx'
import {
  AnimatePresence as RawAnimatePresence,
  motion,
} from 'framer-motion'
import { FC, PropsWithChildren } from 'react'
import { SOCIALS, SocialId, SocialOption } from '../../constants/socials'

// framer-motion 11's AnimatePresence return type collides with the React 18
// JSX.Element type under TS 4.9; this cast keeps the runtime behavior intact.
const AnimatePresence = RawAnimatePresence as FC<
  PropsWithChildren<{ initial?: boolean; mode?: 'sync' | 'wait' | 'popLayout' }>
>

export type SocialState = {
  enabled: Record<SocialId, boolean>
  usernames: Partial<Record<SocialId, string>>
}

type Variant = 'rail' | 'stacked'

type Props = {
  value: SocialState
  onChange: (next: SocialState) => void
  // 'rail'    → compact horizontal pill rail with inputs cascading below
  //             (used on the Friends edit form for power users).
  // 'stacked' → vertical list of toggle rows with switches, inputs reveal
  //             inline (used on the public Meet form so new friends can see
  //             every option without hunting).
  variant?: Variant
}

export function SocialSelector({ value, onChange, variant = 'rail' }: Props) {
  const toggle = (id: SocialId) => {
    onChange({
      ...value,
      enabled: { ...value.enabled, [id]: !value.enabled[id] },
    })
  }
  const setUsername = (id: SocialId, username: string) => {
    onChange({
      ...value,
      usernames: { ...value.usernames, [id]: username },
    })
  }

  if (variant === 'stacked') {
    return (
      <StackedSocials
        value={value}
        toggle={toggle}
        setUsername={setUsername}
      />
    )
  }

  return <RailSocials value={value} toggle={toggle} setUsername={setUsername} />
}

type SubProps = {
  value: SocialState
  toggle: (id: SocialId) => void
  setUsername: (id: SocialId, username: string) => void
}

function RailSocials({ value, toggle, setUsername }: SubProps) {
  const activeWithInputs = SOCIALS.filter(
    (s) => value.enabled[s.id] && s.type === 'username',
  )

  return (
    <div className="space-y-3">
      <div className="-mx-5">
        <div className="no-scrollbar flex gap-2 overflow-x-auto px-5">
          {SOCIALS.map((social) => {
            const on = !!value.enabled[social.id]
            return (
              <button
                key={social.id}
                type="button"
                onClick={() => toggle(social.id)}
                aria-pressed={on}
                className={clsx(
                  'flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-sm transition-colors',
                  on
                    ? 'bg-primary-500 text-black'
                    : 'bg-white/[0.04] text-theme-text-subtle ring-1 ring-white/10',
                )}
              >
                <svg
                  viewBox="0 0 24 24"
                  className={clsx('h-4 w-4', on ? 'text-black' : 'text-white/70')}
                  fill="currentColor"
                  aria-hidden
                >
                  <path d={social.glyph} />
                </svg>
                {social.shortLabel}
              </button>
            )
          })}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {activeWithInputs.length > 0 && (
          <motion.div
            key="inputs"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="space-y-2 pt-1">
              {activeWithInputs.map((social) => (
                <motion.label
                  key={social.id}
                  layout
                  className="flex items-center gap-3 rounded-xl bg-theme-input-bg px-3 py-2 ring-1 ring-theme-input-border focus-within:ring-primary-300"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4 shrink-0 text-theme-text-muted"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d={social.glyph} />
                  </svg>
                  <span className="shrink-0 text-[13px] font-medium text-theme-text-subtle">
                    {social.label}
                  </span>
                  <input
                    type="text"
                    value={value.usernames[social.id] ?? ''}
                    onChange={(e) => setUsername(social.id, e.target.value)}
                    placeholder={social.placeholder}
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    className="min-w-0 flex-1 bg-transparent text-sm text-white placeholder:text-theme-text-muted focus:outline-none"
                  />
                </motion.label>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function StackedSocials({ value, toggle, setUsername }: SubProps) {
  return (
    <div className="space-y-2">
      {SOCIALS.map((social) => (
        <StackedRow
          key={social.id}
          social={social}
          on={!!value.enabled[social.id]}
          username={value.usernames[social.id] ?? ''}
          onToggle={() => toggle(social.id)}
          onUsername={(v) => setUsername(social.id, v)}
        />
      ))}
    </div>
  )
}

function StackedRow({
  social,
  on,
  username,
  onToggle,
  onUsername,
}: {
  social: SocialOption
  on: boolean
  username: string
  onToggle: () => void
  onUsername: (next: string) => void
}) {
  const showInput = on && social.type === 'username'
  return (
    <div className="overflow-hidden rounded-xl bg-theme-input-bg ring-1 ring-theme-input-border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-3.5 py-3 text-left"
      >
        <svg
          viewBox="0 0 24 24"
          className={clsx(
            'h-5 w-5 shrink-0 transition-colors',
            on ? 'text-white' : 'text-theme-text-muted',
          )}
          fill="currentColor"
          aria-hidden
        >
          <path d={social.glyph} />
        </svg>
        <span
          className={clsx(
            'flex-1 text-[15px] transition-colors',
            on ? 'text-white' : 'text-theme-text-subtle',
          )}
        >
          {social.label}
        </span>
        <SwitchVisual on={on} />
      </button>

      <AnimatePresence initial={false}>
        {showInput && (
          <motion.div
            key="input"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="px-2 pb-2 pt-1">
              <input
                type="text"
                value={username}
                onChange={(e) => onUsername(e.target.value)}
                placeholder={social.placeholder}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                onClick={(e) => e.stopPropagation()}
                className="block w-full rounded-xl bg-black/30 px-3.5 py-3 text-[15px] text-white ring-1 ring-white/10 placeholder:text-theme-text-muted focus:outline-none focus:ring-primary-300"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Pure visual switch — the parent button captures the click, so this is a
// span (not a button) to avoid duplicate event firing.
function SwitchVisual({ on }: { on: boolean }) {
  return (
    <span
      role="switch"
      aria-checked={on}
      className={clsx(
        'relative inline-flex h-[28px] w-[48px] shrink-0 items-center rounded-full transition-colors',
        on ? 'bg-primary-500' : 'bg-white/10',
      )}
    >
      <span
        className={clsx(
          'inline-block h-[24px] w-[24px] rounded-full bg-white shadow transition-transform duration-200',
          on ? 'translate-x-[22px]' : 'translate-x-[2px]',
        )}
      />
    </span>
  )
}
